import clsx from 'clsx';
import { Formik, useField } from 'formik';
import {
  ProjectPageGetProjectByIdDocument,
  useEditProjectMutation,
  useProjectPageCompanyQuery,
  useProjectPageGetProjectByIdQuery,
} from 'generated/graphql';
import { useState } from 'react';
import {
  Button,
  EditFormWrapper,
  FormikCheckboxFormField,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './ProjectAiInterviewScoreSettings.module.scss';
import {
  AiInterviewScoreFormProps,
  AiInterviewScoreFormValues,
  AiInterviewScorePreviewProps,
  ProjectAiInterviewScoreSettingsProps,
} from './ProjectAiInterviewScoreSettings.types';

export const AI_INTERVIEW_SCORE_FORM_ID =
  'project-ai-interview-score-settings-form';

export function ProjectAiInterviewScoreSettings({
  projectId,
  companyId,
}: ProjectAiInterviewScoreSettingsProps) {
  const [formActive, setFormActive] = useState(false);
  const { handleMsgType } = useNotification();

  const { data: projectData } = useProjectPageGetProjectByIdQuery({
    variables: { id: projectId },
  });
  const { data: companyData } = useProjectPageCompanyQuery({
    variables: { id: companyId },
  });

  const project = projectData?.project;
  const isAiInterviewScoreHidden = Boolean(project?.isAiInterviewScoreHidden);
  const isAiInterviewScoreHidingAvailable = Boolean(
    companyData?.company?.isAiInterviewScoreHidingAvailable
  );

  const [editProject] = useEditProjectMutation({
    refetchQueries: [
      {
        query: ProjectPageGetProjectByIdDocument,
        variables: { id: projectId },
      },
    ],
    awaitRefetchQueries: true,
  });

  async function handleSave(values: AiInterviewScoreFormValues) {
    if (!project) {
      return;
    }
    try {
      await editProject({
        variables: {
          id: projectId,
          name: project.name,
          companyId,
          isAiInterviewScoreHidden: values.isAiInterviewScoreHidden,
        },
      });
      handleMsgType({
        type: TNotification.success,
        message: 'Successfully updated AI interview scores',
      });
    } catch (error) {
      handleMsgType({
        type: TNotification.error,
        title: 'AI interview scores could not be updated',
        message: (error as Error).message,
      });
    }
    setFormActive(false);
  }

  return (
    <section className={styles.projectSettingSection}>
      {formActive ? (
        <AiInterviewScoreForm
          isAiInterviewScoreHidden={isAiInterviewScoreHidden}
          isAiInterviewScoreHidingAvailable={isAiInterviewScoreHidingAvailable}
          onCancel={() => setFormActive(false)}
          onSubmit={handleSave}
        />
      ) : (
        <AiInterviewScorePreview
          isAiInterviewScoreHidden={isAiInterviewScoreHidden}
          isAiInterviewScoreHidingAvailable={isAiInterviewScoreHidingAvailable}
          activateForm={() => setFormActive(true)}
        />
      )}
    </section>
  );
}

function AiInterviewScorePreview({
  isAiInterviewScoreHidden,
  isAiInterviewScoreHidingAvailable,
  activateForm,
}: AiInterviewScorePreviewProps) {
  return (
    <div>
      <div className="project-header">
        <div className="project-header__left">
          <div className="project-header__sub-title">AI interview scores</div>
        </div>
        <div className="project-header__right">
          <Button onClick={activateForm} size="medium">
            Edit
          </Button>
        </div>
      </div>

      <div className="project-info">
        <div
          className={clsx(
            'project-info__item',
            !isAiInterviewScoreHidingAvailable && styles.disabledSetting
          )}
          data-testid="ai-interview-score-hidden-item"
        >
          <div className="project-info__label">Hide AI interview scores</div>
          <div className="project-info__value">
            {isAiInterviewScoreHidden ? 'On' : 'Off'}
          </div>
        </div>
      </div>
    </div>
  );
}

function AiInterviewScoreForm({
  isAiInterviewScoreHidden,
  isAiInterviewScoreHidingAvailable,
  onSubmit,
  onCancel,
}: AiInterviewScoreFormProps) {
  return (
    <EditFormWrapper
      formId={AI_INTERVIEW_SCORE_FORM_ID}
      title="Edit AI interview scores"
      hasResetAllButton={false}
      onCancelClick={onCancel}
    >
      <Formik<AiInterviewScoreFormValues>
        onSubmit={onSubmit}
        initialValues={{ isAiInterviewScoreHidden }}
      >
        {({ handleSubmit }) => (
          <form
            id={AI_INTERVIEW_SCORE_FORM_ID}
            onSubmit={handleSubmit}
            className={styles.formFieldsWrapper}
          >
            {!isAiInterviewScoreHidingAvailable && (
              <InlineNotification
                notificationType={NotificationType.NEUTRAL}
                className={styles.inlineNotification}
              >
                Allow hiding AI interview scores in the company&apos;s Features
                tab first.
              </InlineNotification>
            )}
            <FormikCheckboxFormField
              id="isAiInterviewScoreHidden"
              name="isAiInterviewScoreHidden"
              label="Hide AI interview scores"
              useFormikField={useField}
              disabled={!isAiInterviewScoreHidingAvailable}
            />
          </form>
        )}
      </Formik>
    </EditFormWrapper>
  );
}
